import { useSyncExternalStore } from "react";

/**
 * The colour theme, per device. `public/theme-init.js` applies the stored choice
 * before first paint (the CSP refuses inline scripts, so it is a file of its own)
 * and carries a copy of the registry below — the ids, appearances, colours and the
 * System choices — and the storage key. `theme.test.ts` runs that script against
 * every stored value and checks it lands where `applyTheme` would, or the page
 * would flash one theme and then switch to another.
 *
 * Everything visual reads CSS variables, so a theme is only `data-theme` on
 * <html> (plus the `.dark` class for the dark ones, so a `dark:` variant still
 * means "a dark background"); `index.css` holds one token set per theme.
 *
 * To add a theme: one block in index.css, one entry in THEMES (and its family in
 * THEME_FAMILIES if it is new), the same in theme-init.js, then run theme.test.ts.
 */
export const THEMES = [
  { id: "dark", family: "board", label: "Dark", hint: "The default", appearance: "dark", color: "#0d0f15" },
  { id: "light", family: "board", label: "Light", hint: "For a bright room", appearance: "light", color: "#fbfbfd" },
  { id: "gray", family: "board", label: "Gray", hint: "Neutral and softer than Dark", appearance: "dark", color: "#262626" },
  { id: "github-light", family: "github", label: "Light", hint: "Primer, as on github.com", appearance: "light", color: "#ffffff" },
  { id: "github-dark", family: "github", label: "Dark", hint: "Primer's dark default", appearance: "dark", color: "#0d1117" },
  { id: "solarized-light", family: "solarized", label: "Light", hint: "Warm cream, low glare", appearance: "light", color: "#eee8d5" },
  { id: "solarized-dark", family: "solarized", label: "Dark", hint: "Deep teal, low glare", appearance: "dark", color: "#002b36" },
  { id: "catppuccin-latte", family: "catppuccin", label: "Latte", hint: "Soft pastel light", appearance: "light", color: "#e6e9ef" },
  { id: "catppuccin-mocha", family: "catppuccin", label: "Mocha", hint: "Soft pastel dark", appearance: "dark", color: "#181825" },
  { id: "nord", family: "nord", label: "Nord", hint: "Arctic blue-grey", appearance: "dark", color: "#252a33" },
  { id: "dracula", family: "dracula", label: "Dracula", hint: "Vivid accents on purple-grey", appearance: "dark", color: "#21222c" },
  { id: "hc-light", family: "hc", label: "Light", hint: "Black on white, for sunlight", appearance: "light", color: "#ffffff" },
  { id: "hc-dark", family: "hc", label: "Dark", hint: "White on black", appearance: "dark", color: "#000000" },
] as const satisfies readonly ThemeDef[];

/**
 * A family is a set of themes shown together, and its System choice — dark or
 * light as the OS is — picks between its `dark` and `light` members. The board's
 * own is plain `system`, the value devices stored before families existed. A
 * family with one theme (Nord, Dracula: dark only) has no System, and `null`s.
 */
export const THEME_FAMILIES = [
  { id: "board", name: "Board", system: "system", light: "light", dark: "dark", hint: "Dark or light, as this device is" },
  { id: "github", name: "GitHub", system: "system:github", light: "github-light", dark: "github-dark", hint: "GitHub Light or Dark, as this device is" },
  { id: "solarized", name: "Solarized", system: "system:solarized", light: "solarized-light", dark: "solarized-dark", hint: "Solarized Light or Dark, as this device is" },
  { id: "catppuccin", name: "Catppuccin", system: "system:catppuccin", light: "catppuccin-latte", dark: "catppuccin-mocha", hint: "Latte or Mocha, as this device is" },
  { id: "nord", name: "Nord", system: null, light: null, dark: null, hint: "" },
  { id: "dracula", name: "Dracula", system: null, light: null, dark: null, hint: "" },
  { id: "hc", name: "High contrast", system: "system:hc", light: "hc-light", dark: "hc-dark", hint: "Black or white, as this device is" },
] as const satisfies readonly ThemeFamilyDef[];

interface ThemeDef {
  id: string;
  family: string;
  /** The name within its family; `themeName` adds the family's for anywhere else. */
  label: string;
  hint: string;
  /** Light-on-dark or dark-on-light: `.dark`, `color-scheme`, the Toaster and the iOS status bar follow it. */
  appearance: "dark" | "light";
  /** The page background, for `<meta name="theme-color">` (the browser and OS chrome). */
  color: string;
}

type ThemeFamilyDef = { id: string; name: string; hint: string } & (
  | { system: string; light: string; dark: string }
  | { system: null; light: null; dark: null }
);

export type ResolvedTheme = (typeof THEMES)[number]["id"];
export type ThemeFamily = (typeof THEME_FAMILIES)[number];
/** A family that has a System choice. */
export type SwitchingFamily = Extract<ThemeFamily, { system: string }>;
export type SystemPref = SwitchingFamily["system"];
export type ThemePref = ResolvedTheme | SystemPref;

export const THEME_STORAGE_KEY = "automation.theme";
const SWITCHING = THEME_FAMILIES.filter((f): f is SwitchingFamily => f.system !== null);
export const THEME_PREFS: readonly ThemePref[] = [...SWITCHING.map((f) => f.system), ...THEMES.map((t) => t.id)];
/** Dark, so a device that never chose looks exactly as it did before themes existed. */
export const DEFAULT_THEME: ThemePref = "dark";

const BY_ID = new Map<string, (typeof THEMES)[number]>(THEMES.map((t) => [t.id, t]));
const BY_SYSTEM = new Map<string, SwitchingFamily>(SWITCHING.map((f) => [f.system, f]));

export const THEME_COLORS = Object.fromEntries(THEMES.map((t) => [t.id, t.color])) as Record<ResolvedTheme, string>;

/** Anything stored that is not a known choice — nothing, or garbage — is the default. */
export function parseThemePref(raw: string | null | undefined): ThemePref {
  return THEME_PREFS.includes(raw as ThemePref) ? (raw as ThemePref) : DEFAULT_THEME;
}

/** The family whose System a choice is, or null for a concrete theme. */
export const systemFamily = (pref: ThemePref): SwitchingFamily | null => BY_SYSTEM.get(pref) ?? null;

/** What a choice looks like right now: a System is its family's dark or light member as the OS is. */
export function resolveTheme(pref: ThemePref, systemDark: boolean): ResolvedTheme {
  const family = systemFamily(pref);
  if (family) return systemDark ? family.dark : family.light;
  return pref as ResolvedTheme;
}

/** Whether a theme is drawn light-on-dark — the Toaster, `color-scheme` and `.dark` follow it. */
export const isDarkTheme = (theme: ResolvedTheme): boolean => BY_ID.get(theme)?.appearance !== "light";

/** A theme's full name outside its family's group: "Dark", "GitHub Light", "Nord". */
export function themeName(theme: ResolvedTheme): string {
  const def = BY_ID.get(theme)!;
  const family = THEME_FAMILIES.find((f) => f.id === def.family)!;
  return def.family === "board" || def.label === family.name ? def.label : `${family.name} ${def.label}`;
}

/** The picker's groups: per family, its System and then its themes in registry order. */
export const THEME_GROUPS = THEME_FAMILIES.map((family) => ({
  family,
  themes: THEMES.filter((t) => t.family === family.id),
}));

const DARK_QUERY = "(prefers-color-scheme: dark)";
const listeners = new Set<() => void>();
let memory: ThemePref | null = null;

function readPref(): ThemePref {
  if (memory) return memory;
  try {
    memory = parseThemePref(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    memory = DEFAULT_THEME;
  }
  return memory;
}

const systemDark = (): boolean =>
  typeof window === "undefined" || !window.matchMedia ? true : window.matchMedia(DARK_QUERY).matches;

/** Puts a resolved theme on the document: the attribute, the class, and the browser chrome's colour. */
export function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const dark = isDarkTheme(theme);
  root.dataset.theme = theme;
  root.classList.toggle("dark", dark);
  root.style.colorScheme = dark ? "dark" : "light";
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.content = THEME_COLORS[theme];
  }
  // Read by iOS at launch only, so it takes effect the next time the installed app opens.
  document
    .querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-status-bar-style"]')
    ?.setAttribute("content", dark ? "black-translucent" : "default");
}

const notify = () => {
  applyTheme(resolveTheme(readPref(), systemDark()));
  listeners.forEach((listener) => listener());
};

export function setThemePref(pref: ThemePref): void {
  memory = pref;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // Private mode: the choice holds for this page.
  }
  notify();
}

if (typeof window !== "undefined") {
  // A System choice follows the OS live, and another tab's choice reaches this one.
  window.matchMedia?.(DARK_QUERY).addEventListener?.("change", () => {
    if (systemFamily(readPref())) notify();
  });
  window.addEventListener("storage", (event) => {
    if (event.key !== THEME_STORAGE_KEY) return;
    memory = null;
    notify();
  });
  // theme-init.js normally did this already; this covers a page loaded without it.
  applyTheme(resolveTheme(readPref(), systemDark()));
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

let lastSnapshot: { pref: ThemePref; resolved: ResolvedTheme } | null = null;
const snapshot = () => {
  const pref = readPref();
  const resolved = resolveTheme(pref, systemDark());
  if (!lastSnapshot || lastSnapshot.pref !== pref || lastSnapshot.resolved !== resolved) lastSnapshot = { pref, resolved };
  return lastSnapshot;
};

/** The chosen theme and what it resolves to now, re-rendering on a change from anywhere. */
export function useTheme(): { pref: ThemePref; resolved: ResolvedTheme; setPref: (pref: ThemePref) => void } {
  const { pref, resolved } = useSyncExternalStore(subscribe, snapshot);
  return { pref, resolved, setPref: setThemePref };
}
